import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@iptv/ui';
import { Activity } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { adminFetch, setTokens } from '../lib/api';

// Internal admin domain used to expand username-only logins into the
// email format the backend's auth endpoint expects. Operators only ever
// see / type the bare username (e.g. `yassine`); the form transparently
// appends @ADMIN_EMAIL_DOMAIN before POSTing.
const ADMIN_EMAIL_DOMAIN = 'kickofftv26.com';

function expandIdentifierToEmail(identifier: string): string {
  const trimmed = identifier.trim();
  if (trimmed.includes('@')) return trimmed.toLowerCase();
  return `${trimmed}@${ADMIN_EMAIL_DOMAIN}`.toLowerCase();
}

export function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await adminFetch<{ access_token: string; refresh_token: string }>(
        '/api/v1/auth/login',
        {
          method: 'POST',
          body: { email: expandIdentifierToEmail(identifier), password },
        },
      );
      setTokens(data.access_token, data.refresh_token);
      navigate('/');
    } catch (err) {
      setError((err as { detail?: string }).detail ?? 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mb-2 flex items-center justify-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            <CardTitle className="text-xl">IPTV Admin</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">Sign in to your operator account.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="identifier">Username</Label>
              <Input
                id="identifier"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
